import { CartLinesUpdateEvent, StandardEvents } from "@shopify/events";

const GIFT_PRODUCT_ID = 8199480901701;
const GIFT_VARIANT_ID = 45431104897093;
const GIFT_PRODUCT_LINK = '/products/delicate-gift';
function hasQualifyingProduct(cart) {
  return cart.items.some((item) => Number(item.product_id) !== GIFT_PRODUCT_ID);
}

const ROOT = window.Shopify?.routes?.root || '/';
const NOTICE_CLASS = 'pq-free-gift-notice';

let syncing = false;
let syncQueued = false;
let giftSuppressed = false;
let visibilityObserver;

function injectStyles() {
  if (document.getElementById('pq-free-gift-styles')) return;

  const style = document.createElement('style');
  style.id = 'pq-free-gift-styles';
  style.textContent = `
    .${NOTICE_CLASS} {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 10px;
      align-items: start;
      margin: 0 0 16px;
      padding: 14px 16px;
      border: 1px solid rgba(20, 20, 20, .16);
      border-radius: 12px;
      background: #fff8fb;
      color: #181818;
      font-size: 14px;
      line-height: 1.45;
    }
    .${NOTICE_CLASS}__icon { font-size: 22px; line-height: 1; }
    .${NOTICE_CLASS} strong { display: block; margin-bottom: 2px; font-size: 14px; }
    .${NOTICE_CLASS}[hidden] { display: none !important; }
    .pq-free-gift-eligible-product .shopify-payment-button shopify-accelerated-checkout { display: none !important; }
    .pq-free-gift-eligible-product .shopify-payment-button { display: block !important; width: 100% !important; }
    .pq-real-buy-now { display: flex !important; align-items: center; justify-content: center; width: 100%; height: 52px; border: 0; border-radius: 999px; background: #fc6403; color: #fff; font-size: 16px; font-weight: 800; cursor: pointer; }
    .pq-real-buy-now:disabled { opacity: .65; cursor: wait; }
    @media (max-width: 749px) {
      .pq-free-gift-eligible-product form[data-type="add-to-cart-form"] .shopify-payment-button {
        display: block !important;
        position: static !important;
        inset: auto !important;
        z-index: auto !important;
        transform: none !important;
        grid-area: auto / 1 / auto / -1 !important;
        grid-column: 1 / -1 !important;
        width: 100% !important;
        height: 52px !important;
        margin-top: 10px;
      }
      .pq-real-buy-now { border-radius: 10px; }
    }
  `;
  document.head.appendChild(style);
}

function createNotice(kind) {
  const notice = document.createElement('div');
  notice.className = NOTICE_CLASS;
  notice.dataset.pqFreeGiftNotice = kind;
  notice.setAttribute('role', 'status');
  notice.innerHTML = `
    <span class="${NOTICE_CLASS}__icon" aria-hidden="true">🎁</span>
    <span>
      <strong>Free gift included</strong>
      ${
        kind === 'product'
          ? 'Add this product to your cart and we’ll automatically include one complimentary gift.'
          : kind === 'conflict'
            ? 'The complimentary gift is currently unavailable with this cart.'
            : 'Your complimentary gift has been added automatically. One free gift per order.'
      }
    </span>
  `;
  return notice;
}

function giftLinksWithin(root = document) {
  const selector = `a[href*="${GIFT_PRODUCT_LINK}"]`;
  const links = [];

  if (root instanceof Element && root.matches(selector)) links.push(root);
  if ('querySelectorAll' in root) links.push(...root.querySelectorAll(selector));

  return links;
}

function hideGiftProductCards(root = document) {
  giftLinksWithin(root).forEach((link) => {
    if (link.closest('cart-items-component')) return;

    const displayContainer =
      link.closest('.resource-list__item') ||
      link.closest('li.predictive-search-results__card--product') ||
      link.closest('li.menu-drawer__featured-content-list-item--product') ||
      link.closest('li.product-grid__item') ||
      link.closest('product-card') ||
      link.closest('product-card-link');

    if (!(displayContainer instanceof HTMLElement) || displayContainer.hidden) return;

    displayContainer.hidden = true;
    displayContainer.setAttribute('aria-hidden', 'true');
    displayContainer.style.setProperty('display', 'none', 'important');
  });
}

function observeGiftProductCards() {
  if (!document.body || visibilityObserver) return;

  visibilityObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) {
          hideGiftProductCards(node);
          prepareSafeBuyNowButtons(node);
        }
      });
    });
  });

  visibilityObserver.observe(document.body, { childList: true, subtree: true });
}

function currentProductId() {
  const main = document.querySelector('main[data-template^="product"]');
  if (!main) return null;

  const source = main.querySelector(
    'product-form-component[data-product-id], sticky-add-to-cart[data-product-id]'
  );
  return source instanceof HTMLElement ? Number(source.dataset.productId) : null;
}

function renderProductNotice() {
  const productId = currentProductId();
  if (!productId || productId === GIFT_PRODUCT_ID) return;

  document.documentElement.classList.add('pq-free-gift-eligible-product');
  if (document.querySelector('[data-pq-free-gift-notice="product"]')) return;

  const details = document.querySelector('[data-testid="product-information"] .product-details');
  if (!(details instanceof HTMLElement)) return;

  const content = details.querySelector('[data-testid="group-block"]');
  (content instanceof HTMLElement ? content : details).prepend(createNotice('product'));
}

function renderCartNotices(cart, conflict = false) {
  const eligible = hasQualifyingProduct(cart);

  document
    .querySelectorAll('[data-pq-free-gift-notice="cart"], [data-pq-free-gift-notice="conflict"]')
    .forEach((notice) => notice.remove());

  if (!eligible) return;

  document.querySelectorAll('cart-items-component').forEach((component) => {
    if (component instanceof HTMLElement) {
      component.prepend(createNotice(conflict ? 'conflict' : 'cart'));
    }
  });
}

function renderCartNoticesAfterUpdate(cart, conflict = false) {
  renderCartNotices(cart, conflict);
  window.requestAnimationFrame(() => renderCartNotices(cart, conflict));
}

async function getCart() {
  const response = await fetch(`${ROOT}cart.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error(`Unable to read cart (${response.status})`);
  return response.json();
}

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : null))
    .filter(Boolean);
}

async function mutateCart(endpoint, payload, action, lines) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'pq-free-gift',
      lines,
      promise: deferred.promise,
    })
  );

  try {
    const sections = cartSectionIds();
    const body = { ...payload, sections, sections_url: window.location.pathname };
    const response = await fetch(`${ROOT}${endpoint}`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    const responseData = await response.json();

    if (!response.ok || responseData.status) {
      throw new Error(
        responseData.description || responseData.message || `Cart update failed (${response.status})`
      );
    }

    const cart = endpoint === 'cart/change.js' ? responseData : await getCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        sections: responseData.sections || {},
        source: 'pq-free-gift',
        sourceId: 'pq-free-gift',
        itemCount: 1,
        productId: String(GIFT_PRODUCT_ID),
        didError: false,
      },
    });
    return cart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

async function syncGift() {
  if (syncing) {
    syncQueued = true;
    return;
  }

  syncing = true;

  try {
    let cart = await getCart();
    const hasQualifyingItem = hasQualifyingProduct(cart);
    let giftLine = cart.items.find((item) => Number(item.product_id) === GIFT_PRODUCT_ID);
    let discountConflict = false;

    if (hasQualifyingItem && !giftLine && !giftSuppressed) {
      cart = await mutateCart(
        'cart/add.js',
        {
          items: [
            {
              id: GIFT_VARIANT_ID,
              quantity: 1,
              properties: { _pq_free_gift: 'Included with product order' },
            },
          ],
        },
        'add',
        [{ merchandiseId: String(GIFT_VARIANT_ID), quantity: 1 }]
      );

      giftLine = cart.items.find((item) => Number(item.product_id) === GIFT_PRODUCT_ID);

      if (giftLine && Number(giftLine.final_line_price) > 0) {
        giftSuppressed = true;
        discountConflict = true;
        cart = await mutateCart(
          'cart/change.js',
          { id: giftLine.key, quantity: 0 },
          'change',
          [{ merchandiseId: String(GIFT_VARIANT_ID), quantity: 0 }]
        );
        giftLine = null;
      }
    } else if (!hasQualifyingItem && giftLine) {
      giftSuppressed = false;
      cart = await mutateCart(
        'cart/change.js',
        { id: giftLine.key, quantity: 0 },
        'change',
        [{ merchandiseId: String(GIFT_VARIANT_ID), quantity: 0 }]
      );
    } else if (giftLine && Number(giftLine.quantity) !== 1) {
      cart = await mutateCart(
        'cart/change.js',
        { id: giftLine.key, quantity: 1 },
        'change',
        [{ merchandiseId: String(GIFT_VARIANT_ID), quantity: 1 }]
      );
    } else if (hasQualifyingItem && giftSuppressed) {
      discountConflict = true;
    }

    giftLine = cart.items.find((item) => Number(item.product_id) === GIFT_PRODUCT_ID);
    if (hasQualifyingItem && giftLine && Number(giftLine.final_line_price) > 0) {
      giftSuppressed = true;
      discountConflict = true;
      cart = await mutateCart(
        'cart/change.js',
        { id: giftLine.key, quantity: 0 },
        'change',
        [{ merchandiseId: String(GIFT_VARIANT_ID), quantity: 0 }]
      );
    }

    renderCartNoticesAfterUpdate(cart, discountConflict);
    hideGiftProductCards();
  } catch (error) {
    console.warn('[Perfect Queen free gift]', error);
  } finally {
    syncing = false;
    if (syncQueued) {
      syncQueued = false;
      queueMicrotask(syncGift);
    }
  }
}

function scheduleSync() {
  if (syncQueued) return;

  syncQueued = true;
  queueMicrotask(() => {
    syncQueued = false;
    syncGift();
  });
}

async function waitUntilGiftReady(timeout = 6000) {
  const deadline = Date.now() + timeout;

  while (Date.now() < deadline) {
    if (!syncing) await syncGift();

    const cart = await getCart();
    if (!hasQualifyingProduct(cart)) return true;

    const giftLine = cart.items.find((item) => Number(item.product_id) === GIFT_PRODUCT_ID);
    if (
      giftLine &&
      Number(giftLine.quantity) === 1 &&
      Number(giftLine.final_line_price) === 0
    ) {
      return true;
    }

    await new Promise((resolve) => window.setTimeout(resolve, 150));
  }

  return false;
}

function activeProductForm(button) {
  const directForm = button.closest('form[data-type=\"add-to-cart-form\"], form.shopify-product-form');
  if (directForm instanceof HTMLFormElement) return directForm;

  const forms = [...document.querySelectorAll('main[data-template^=\"product\"] form[data-type=\"add-to-cart-form\"]')];
  return forms.find((form) => form.querySelector('quantity-selector-component[data-variant-id]')) || forms[0] || null;
}

function selectedProductVariantId(form) {
  const quantitySelector = form.querySelector('quantity-selector-component[data-variant-id]');
  const quantityVariantId = Number(quantitySelector?.dataset.variantId);
  if (quantityVariantId > 0) return quantityVariantId;

  const selectedVariant = document.querySelector('main[data-template^=\"product\"] input[data-variant-id]:checked');
  const selectedVariantId = Number(selectedVariant?.dataset.variantId);
  if (selectedVariantId > 0) return selectedVariantId;

  const hiddenVariantId = Number(form.querySelector('input[name=\"id\"]')?.value);
  return hiddenVariantId > 0 ? hiddenVariantId : null;
}

async function handleSafeBuyNow(event) {
  const button = event.currentTarget;
  if (!(button instanceof HTMLButtonElement)) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  if (button.dataset.pqGiftCheckoutPending === 'true') return;

  const form = activeProductForm(button);
  const addButton = form?.querySelector('button[name=\"add\"], input[name=\"add\"]');
  const variantId = form ? selectedProductVariantId(form) : null;

  if (!(form instanceof HTMLFormElement) || !variantId || addButton?.disabled) {
    button.textContent = addButton?.disabled ? 'Unavailable' : 'Please select options';
    return;
  }

  const quantity = Math.max(1, Number(form.querySelector('input[name=\"quantity\"]')?.value) || 1);
  button.dataset.pqGiftCheckoutPending = 'true';
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  button.textContent = 'Adding gift...';

  try {
    if (button.dataset.pqProductAdded !== 'true') {
      const response = await fetch(ROOT + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
      });
      const responseData = await response.json();

      if (!response.ok || responseData.status) {
        throw new Error(responseData.description || responseData.message || 'Unable to add this product');
      }

      button.dataset.pqProductAdded = 'true';
    }

    giftSuppressed = false;
    if (!(await waitUntilGiftReady(8000))) {
      throw new Error('The complimentary gift could not be prepared');
    }

    window.location.assign(ROOT + 'checkout');
  } catch (error) {
    console.warn('[Perfect Queen safe buy now]', error);
    button.disabled = false;
    button.removeAttribute('aria-busy');
    delete button.dataset.pqGiftCheckoutPending;
    button.textContent = button.dataset.pqProductAdded === 'true' ? 'Continue to checkout' : 'Try again';
  }
}

function prepareSafeBuyNowButtons(root = document) {
  const containers = [];

  if (root instanceof Element && root.matches('.shopify-payment-button')) containers.push(root);
  if ('querySelectorAll' in root) containers.push(...root.querySelectorAll('.shopify-payment-button'));

  containers.forEach((container) => {
    let button = container.querySelector('.pq-real-buy-now');
    if (!(button instanceof HTMLButtonElement)) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'pq-real-buy-now';
      button.textContent = 'Buy Now';
      container.appendChild(button);
    }

    const form = activeProductForm(button);
    const buttonGroup = form?.querySelector('[ref="productFormButtons"], .product-form-buttons');
    const mobileLayout = window.matchMedia('(max-width: 749px)').matches;
    const mobileProperties = ['position', 'inset', 'z-index', 'transform', 'width', 'height', 'margin-top'];

    if (mobileLayout) {
      if (buttonGroup instanceof HTMLElement && container.parentElement !== buttonGroup) {
        buttonGroup.appendChild(container);
      }
      container.style.setProperty('position', 'static', 'important');
      container.style.setProperty('inset', 'auto', 'important');
      container.style.setProperty('z-index', 'auto', 'important');
      container.style.setProperty('transform', 'none', 'important');
      container.style.setProperty('width', '100%', 'important');
      container.style.setProperty('height', '52px', 'important');
      container.style.setProperty('margin-top', '10px', 'important');
    } else {
      mobileProperties.forEach((property) => container.style.removeProperty(property));
    }

    button.onclick = null;
    if (button.dataset.pqGiftSafeBound === 'true') return;
    button.dataset.pqGiftSafeBound = 'true';
    button.setAttribute('aria-label', 'Buy Now — free gift included');
    button.addEventListener('click', handleSafeBuyNow, true);
  });
}
window.addEventListener('resize', () => prepareSafeBuyNowButtons());
window.matchMedia('(max-width: 749px)').addEventListener?.('change', () => prepareSafeBuyNowButtons());

document.addEventListener(
  'click',
  async (event) => {
    if (!(event.target instanceof Element)) return;

    const checkoutControl = event.target.closest(
      'button[name="checkout"], input[name="checkout"], a[href*="/checkout"]'
    );
    if (
      !(checkoutControl instanceof HTMLElement) ||
      checkoutControl.dataset.pqGiftCheckoutPending === 'true'
    ) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    checkoutControl.dataset.pqGiftCheckoutPending = 'true';
    checkoutControl.setAttribute('aria-busy', 'true');

    try {
      if (await waitUntilGiftReady()) {
        const href =
          checkoutControl instanceof HTMLAnchorElement ? checkoutControl.href : `${ROOT}checkout`;
        window.location.assign(href);
        return;
      }
    } catch (error) {
      console.warn('[Perfect Queen free gift]', error);
    }

    checkoutControl.removeAttribute('aria-busy');
    delete checkoutControl.dataset.pqGiftCheckoutPending;
  },
  true
);

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (syncing) return;

  giftSuppressed = false;
  if (event.promise && typeof event.promise.then === 'function') {
    event.promise.then(scheduleSync).catch(() => {});
  } else {
    scheduleSync();
  }
});

function initialize() {
  injectStyles();
  hideGiftProductCards();
  observeGiftProductCards();
  renderProductNotice();
  prepareSafeBuyNowButtons();
  scheduleSync();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialize, { once: true });
} else {
  initialize();
}

window.addEventListener('pageshow', () => {
  hideGiftProductCards();
  prepareSafeBuyNowButtons();
  scheduleSync();
});
